import { Alert, Box, Paper, TextField, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useBasicStep } from './useBasicStep';
import { useAppDispatch } from '../../../../lib/store';
import { Dropdown } from '../../../../wrapper-components/Dropdown/Dropdown';
import { setProjectName } from '../../state/createProjectSlice';

interface BasicsStepProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}
export const BasicsStep = ({ setRequiredFieldsFilled }: BasicsStepProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const {
    projectName,
    conditionAreaOptions,
    isConditionAreasLoading,
    handleConditionAreaChange,
    conditionArea,
    organizationOptions,
    handleHealthSystemNameChange,
    isOrganizationsLoading,
    healthSystem,
    duplicateError,
  } = useBasicStep({ setRequiredFieldsFilled });
  return (
    <Box id="basics-step">
      <Paper
        sx={{
          p: theme.spacing(6),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(6),
        }}
      >
        <Typography variant="h6">
          {formatMessage({ id: 'createProjectWizard.step.basics.title' })}
        </Typography>
        <Box id="project-name-input-section">
          <TextField
            value={projectName}
            onChange={(event) => dispatch(setProjectName(event.target.value))}
            id="project-name"
            label={formatMessage({ id: 'createProjectWizard.step.basics.projectName' })}
            required
            placeholder={formatMessage({
              id: 'createProjectWizard.step.basics.projectNamePlaceholder',
            })}
            sx={{ width: '100%', '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
          />

          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mt: theme.spacing(1) }}
          >
            {formatMessage({ id: 'createProjectWizard.step.basics.projectNameDescription' })}
          </Typography>
        </Box>
        <Box id="condition-area-input-section">
          <Dropdown
            id="condition-area-dropdown"
            options={conditionAreaOptions}
            disabled={isConditionAreasLoading}
            onChange={handleConditionAreaChange}
            label={formatMessage({ id: 'createProjectWizard.step.basics.conditionArea' })}
            placeholder={formatMessage({
              id: 'createProjectWizard.step.basics.conditionAreaPlaceholder',
            })}
            required
            width="medium"
            value={conditionArea?.id ?? ''}
            sx={{ width: '100%', '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
          />
        </Box>
        <Box id="health-system-name-input-section">
          <Dropdown
            id="health-system-name-dropdown"
            options={organizationOptions}
            onChange={handleHealthSystemNameChange}
            label={formatMessage({ id: 'createProjectWizard.step.basics.healthSystemName' })}
            placeholder={formatMessage({
              id: 'createProjectWizard.step.basics.healthSystemNamePlaceholder',
            })}
            width="medium"
            required
            disabled={isOrganizationsLoading}
            value={healthSystem?.id ?? ''}
            sx={{ width: '100%', '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
          />
        </Box>
        {duplicateError && (
          <Alert severity="error" sx={{ mt: theme.spacing(1) }}>
            {formatMessage({ id: duplicateError })}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};
